import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { MailWarning, Loader2, X } from 'lucide-react';

const DISMISS_KEY = 'email-confirm-banner-dismissed';

export default function EmailConfirmationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [sending, setSending] = useState(false);

  // Only nag landlords who explicitly have an unconfirmed email (missing field = treated as confirmed)
  if (!user || user.role !== 'landlord' || user.email_confirmed !== false || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      const res = await authAPI.resendConfirmation();
      toast.success(res.data?.message || 'Confirmation email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the confirmation email');
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 sm:px-5"
      data-testid="email-confirm-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400/25 text-amber-600 dark:text-amber-400">
            <MailWarning className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Please confirm your email address</p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">
              We sent a confirmation link to <span className="font-medium">{user.email}</span>. Confirming helps keep your account secure — it's optional and you can keep using U-DORM in the meantime.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 sm:pl-3">
          <Button
            size="sm"
            onClick={handleResend}
            disabled={sending}
            className="h-8 bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="email-confirm-resend-btn"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Resend email
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="h-8 w-8 text-amber-700/70 hover:text-amber-900 dark:text-amber-400/70"
            data-testid="email-confirm-dismiss-btn"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
